import { closestCenter, DndContext, DragEndEvent, DragStartEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useVirtualizer } from '@tanstack/react-virtual';
import { message } from 'antd';
import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { analytics } from '../../../analytics/analytics';
import { AnalyticsEvent } from '../../../analytics/analyticsEvent';
import { taskCompletedProperties } from '../../../analytics/analyticsHelpers';
import { updateTask, updateTaskLabels } from '../../../api/task.api';
import { useGetUserFromAccessToken } from '../../../common/auth';
import { WorkspaceAccessResponse } from '../../../api/workspace';
import { ModalContext, ModalType } from '../../../context/ModalContext';
import { useScrollRestorationContext } from '../../../context/ScrollRestorationContext';
import { useExpandedState } from '../../../hooks/useExpandedState';
import { useNavigateToTask } from '../../../hooks/useTaskNavigation';
import { type FlattenedRow, useVirtualizedTaskData } from '../../../hooks/useVirtualizedTaskData';
import { useViewContext } from '../../../pages/Space/Context/ViewContext';
import { queryClient } from '../../../queryClient';
import { updateSpaceEpicsOrder, updateSpaceTasksOrder } from '../../../serverState/helpers/tasksOrderHelper';
import { reconcileStageChange } from '../../../serverState/helpers/taskStageCache';
import { usePlanningSession } from '../../../serverState/planning';
import { getCurrentWorkspaceActiveSprint } from '../../../serverState/sprint';
import { useCurrentWorkspaceEpics } from '../../../serverState/epics';
import { useUpdateTaskMutation } from '../../../serverState/tasks';
import { useGetWorkspace } from '../../../serverState/workspace';
import { Epic } from '../../../types/Epic';
import Task from '../../../types/Task';
import { defaultTaskStatuses, TaskStatus, TaskStatusGroup } from '../../../types/TaskStatus';
import { TaskType } from '../../../types/TaskType';
import { User } from '../../../types/User';
import Workspace, { WorkspaceEstimationType, WorkspaceFramework } from '../../../types/Workspace';
import { WorkspaceViewEntityType } from '../../../types/WorkspaceView';
import { Column } from '../../../types/WorkspaceViewColumn';
import { GroupItem } from '../../Backlog/TaskGroupSection/TaskGroupSection';
import { SwitchToManualSortModal } from '../../Backlog/TaskGroupSection/Modals/SwitchToManualSort';
import { sidebarWidth } from '../../../constants/styles/mixins';
import { TaskTableDataType } from '../../TaskTable/types/TaskTableTypes';
import { isSameSortGroup as isSameSortGroupFn, calculateNonTitleWidth } from '../../../utils/taskListUtils';
import { ALL_TASKS_VIEW_ID } from '../../../pages/AllTasks/allTasksView';
import { StickyColumnHeader } from './StickyColumnHeader';
import { VirtualizedGroupHeader } from './VirtualizedGroupHeader';
import { VirtualizedTaskRow } from './VirtualizedTaskRow';
import { type RowWorkspaceData } from './types';
import { FLOATING_ACTION_BAR_SPACE } from '../../../constants/floatingActionBar';
import { estimateRowSize, GROUP_HEADER_GAP, isFirstInSection as isFirstRowInSection } from './rowSizing';

export type { RowWorkspaceData };

interface VirtualizedTaskListProps {
  tasksPerGroup: Array<{ group: GroupItem; tasks: TaskTableDataType[] }>;
  unassignedTasks: TaskTableDataType[];
  showUnassignedGroup: boolean;
  groupBy: string;
  usersWithAccessToSpace: WorkspaceAccessResponse[];
  showMultiselect: boolean;
  selectedItems: number[];
  setSelectedItems: (items: number[]) => void;
  customRowSelection?: {
    selectedRowKeysObject: { [id: number]: boolean };
    addTaskToSelectedKeys: (task: Task) => void;
    removeTaskFromSelectedKeys: (task: Task) => void;
  };
  taskMenuProps: {
    items: any[];
    onClick: any;
  };
  onSwitchToManualSort?: (activeTaskId: number, overTaskId: number) => void;
  dataSource: TaskTableDataType[];
  setDataSource: React.Dispatch<React.SetStateAction<TaskTableDataType[]>>;
  handleEditEstimationSettings?: () => void;
  onSprintToPlanningConfirm?: (task: Task) => void;
  viewId: number;
  searchValue?: string;
  filtersSignature?: string;
  expandedStateStorageKey?: string;
  resolveWorkspaceData?: (workspaceId: number) => RowWorkspaceData | undefined;
  resolveSpaceName?: (workspaceId: number) => string;
  showSpaceColumn?: boolean;
  readOnlyOrder?: boolean;
  hideAddTaskRow?: boolean;
  hideGroupActions?: boolean;
}

export const VirtualizedTaskList: React.FC<VirtualizedTaskListProps> = ({
  tasksPerGroup,
  unassignedTasks,
  showUnassignedGroup,
  groupBy,
  usersWithAccessToSpace,
  showMultiselect,
  selectedItems,
  setSelectedItems,
  customRowSelection,
  taskMenuProps,
  onSwitchToManualSort,
  dataSource,
  setDataSource,
  handleEditEstimationSettings,
  onSprintToPlanningConfirm,
  viewId,
  searchValue,
  filtersSignature,
  expandedStateStorageKey,
  resolveWorkspaceData,
  resolveSpaceName,
  showSpaceColumn,
  readOnlyOrder,
  hideAddTaskRow,
  hideGroupActions,
}) => {
  const workspaceQuery = useGetWorkspace();
  const viewContext = useViewContext();
  const navigateToTask = useNavigateToTask();
  const scrollRestorationContext = useScrollRestorationContext();
  const updateTaskMutation = useUpdateTaskMutation();
  const { openModal } = useContext(ModalContext);
  const [messageApi, contextHolder] = message.useMessage();

  const taskStatuses: Record<TaskStatus['id'], TaskStatus> = useMemo(
    () =>
      ((workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses).reduce(
        (prev, curr) => ({ ...prev, [curr.id]: curr }),
        {}
      ),
    [workspaceQuery.data?.TaskStatus]
  );

  const framework = workspaceQuery.data?.framework;
  const estimationType = workspaceQuery.data?.estimationType ?? WorkspaceEstimationType.storyPoints;
  const activeSprintId = workspaceQuery.data?.activeSprintId;
  const planningSessionQuery = usePlanningSession();
  const epicsQuery = useCurrentWorkspaceEpics();
  const activeSprintQuery = getCurrentWorkspaceActiveSprint();
  const planningSessionId = (planningSessionQuery.data as any)?.session?.id;

  // Stable workspace data object for rows — avoids per-row query subscriptions
  const workspaceDataForRows = useMemo((): RowWorkspaceData => ({
    framework,
    estimationType,
    taskStatuses,
    labels: workspaceQuery.data?.Labels || [],
    epics: epicsQuery.data || [],
    activeSprintId: activeSprintQuery.data?.sprint?.id,
    planningSessionId,
    members: usersWithAccessToSpace.map((a) => a.User).filter((u): u is User => !!u),
  }), [framework, estimationType, taskStatuses, workspaceQuery.data?.Labels, epicsQuery.data, activeSprintQuery.data?.sprint?.id, planningSessionId, usersWithAccessToSpace]);

  // Expanded state management
  const {
    expandedGroups,
    expandedSubtasks,
    toggleGroup,
    toggleSubtask,
    loadExpandedState,
    isInitialized,
  } = useExpandedState({ viewId, ...(expandedStateStorageKey ? { storageKey: expandedStateStorageKey } : {}) });

  // Load expanded state on mount
  useEffect(() => {
    void loadExpandedState();
  }, [loadExpandedState]);

  // Flatten data for virtualization
  const computedFlattenedRows = useVirtualizedTaskData({
    tasksPerGroup,
    unassignedTasks,
    expandedGroups,
    expandedSubtasks,
    groupBy,
    showUnassignedGroup,
    taskStatuses,
    hideAddTaskRow,
  });

  // Local state for immediate drag updates (optimistic UI)
  const [localFlattenedRows, setLocalFlattenedRows] = useState<FlattenedRow[]>(computedFlattenedRows);
  const isDraggingRef = useRef(false);

  // Sync local state with computed rows when not dragging
  useEffect(() => {
    if (!isDraggingRef.current) {
      setLocalFlattenedRows(computedFlattenedRows);
    }
  }, [computedFlattenedRows]);

  // Use local state for rendering
  const flattenedRows = localFlattenedRows;

  // Scroll container ref
  const parentRef = useRef<HTMLDivElement>(null);

  // Row ids currently in title-rename mode. Non-empty while renaming, so the wheel/touch
  // handler below can block scrolling — otherwise a row scrolling out of the virtualized
  // viewport unmounts mid-edit and silently discards the unsaved title (#2210).
  const editingRowIdsRef = useRef<Set<number>>(new Set());
  const handleEditingTitleChange = useCallback((taskId: number, isEditing: boolean) => {
    if (isEditing) {
      editingRowIdsRef.current.add(taskId);
    } else {
      editingRowIdsRef.current.delete(taskId);
    }
  }, []);

  useEffect(() => {
    const scrollContainer = parentRef.current;
    if (!scrollContainer) return;

    const blockScrollWhileEditing = (e: Event) => {
      if (editingRowIdsRef.current.size > 0) {
        e.preventDefault();
      }
    };

    scrollContainer.addEventListener('wheel', blockScrollWhileEditing, { passive: false });
    scrollContainer.addEventListener('touchmove', blockScrollWhileEditing, { passive: false });
    return () => {
      scrollContainer.removeEventListener('wheel', blockScrollWhileEditing);
      scrollContainer.removeEventListener('touchmove', blockScrollWhileEditing);
    };
  }, [isInitialized]);

  // Register parentRef as the scroll container for scroll restoration.
  // Depend on isInitialized so we re-register once the scroll div actually mounts.
  useEffect(() => {
    if (parentRef.current) {
      scrollRestorationContext?.registerScrollRef(parentRef);
    }
    return () => {
      scrollRestorationContext?.registerScrollRef(null);
    };
  }, [scrollRestorationContext, isInitialized]);

  // Offset saved when a task was opened from this view, read once on mount. It is the
  // virtualizer's initialOffset, so the virtualizer renders that slice and scrolls the
  // container there in its own layout effect when the scroll div mounts, before the
  // first paint (DEV-3227).
  const [restoreOffset] = useState(() => scrollRestorationContext?.peekRestoreOffset() ?? null);

  // The scroll div mounts once isInitialized flips. By this layout effect the virtualizer
  // has already applied restoreOffset, so just consume it.
  useLayoutEffect(() => {
    if (isInitialized && parentRef.current) {
      scrollRestorationContext?.markRestored();
    }
  }, [isInitialized, scrollRestorationContext]);

  // Scroll to top when search value changes
  useEffect(() => {
    if (parentRef.current && searchValue !== undefined) {
      parentRef.current.scrollTop = 0;
    }
  }, [searchValue]);

  // Scroll to top when the applied filters change (DEV-2524)
  useEffect(() => {
    if (parentRef.current && filtersSignature !== undefined) {
      parentRef.current.scrollTop = 0;
    }
  }, [filtersSignature]);

  // Track which group is currently being hovered for showing dropdown/add buttons
  const [hoveredGroupId, setHoveredGroupId] = useState<string | null>(null);

  // Modal state for switching to manual sort on drag
  const [breakSortOpen, setBreakSortOpen] = useState(false);
  const [pendingDragIds, setPendingDragIds] = useState<{ activeId: number; overId: number } | null>(null);

  // Track active drag for z-index and section boundary restriction
  const [activeDragId, setActiveDragId] = useState<number | null>(null);
  const sectionBoundsRef = useRef<{ minY: number; maxY: number } | null>(null);

  // Ref for the sticky header container to sync scroll directly
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const stickyHeaderWrapperRef = useRef<HTMLDivElement>(null);

  // Sync horizontal scroll position of sticky header with scroll container
  useEffect(() => {
    if (!parentRef.current) return;

    const handleScroll = () => {
      if (!parentRef.current) return;

      const scrollLeft = parentRef.current.scrollLeft;

      // Synchronously update sticky header scroll position (no state, direct DOM manipulation)
      if (stickyHeaderRef.current) {
        stickyHeaderRef.current.scrollLeft = scrollLeft;
      }

      // Toggle horizontal scroll shadow class (direct DOM manipulation for performance)
      if (scrollLeft > 0) {
        parentRef.current.classList.add('is-scrolled-horizontal');
        stickyHeaderRef.current?.classList.add('is-scrolled-horizontal');
      } else {
        parentRef.current.classList.remove('is-scrolled-horizontal');
        stickyHeaderRef.current?.classList.remove('is-scrolled-horizontal');
      }

      // Toggle vertical scroll shadow on sticky header wrapper (not the overflow div itself)
      const scrollTop = parentRef.current.scrollTop;
      if (scrollTop > 0) {
        stickyHeaderWrapperRef.current?.classList.add('is-scrolled-vertical');
      } else {
        stickyHeaderWrapperRef.current?.classList.remove('is-scrolled-vertical');
      }
    };

    const scrollContainer = parentRef.current;
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [isInitialized]);

  // Forward wheel events from sticky header to body scroll container
  useEffect(() => {
    const header = stickyHeaderRef.current;
    const body = parentRef.current;
    if (!header || !body) return;

    const handleWheel = (e: WheelEvent) => {
      // Forward horizontal scroll (or shift+vertical) to body
      const deltaX = e.deltaX || (e.shiftKey ? e.deltaY : 0);
      if (deltaX !== 0) {
        e.preventDefault();
        body.scrollLeft += deltaX;
      }
    };

    header.addEventListener('wheel', handleWheel, { passive: false });
    return () => header.removeEventListener('wheel', handleWheel);
  }, [isInitialized]);

  // Virtualizer setup
  const getItemKey = useCallback(
    (index: number) => flattenedRows[index]?.id ?? String(index),
    [flattenedRows]
  );

  const virtualizer = useVirtualizer({
    count: flattenedRows.length,
    getScrollElement: () => parentRef.current,
    getItemKey,
    estimateSize: (index) => estimateRowSize(flattenedRows, index),
    initialOffset: restoreOffset ?? 0,
    overscan: 10,
  });

  // DnD setup
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // Get all task IDs for selection. Borrowed/context rows (#2309) appear in groups they
  // don't belong to and are read-only — exclude them so they don't pollute selection or
  // produce duplicate dnd-kit sortable ids across groups.
  const allTaskIds = useMemo(() => {
    return flattenedRows
      .filter(
        (row) =>
          (row.type === 'task' || row.type === 'subtask') && !(row as any).task.isGroupContext
      )
      .map((row) => (row as any).task.id);
  }, [flattenedRows]);

  // Pre-compute task group membership for efficient collision detection
  const taskGroupMap = useMemo(() => {
    const map = new Map<number, { type: 'task' | 'subtask'; groupId?: string; parentId?: number }>();
    flattenedRows.forEach((row) => {
      if (row.type !== 'task' && row.type !== 'subtask') return;
      if ((row as any).task.isGroupContext) return; // borrowed context: not draggable
      if (row.type === 'task') {
        map.set((row as any).task.id, { type: 'task', groupId: (row as any).groupId });
      } else if (row.type === 'subtask') {
        map.set((row as any).task.id, { type: 'subtask', parentId: (row as any).parentId });
      }
    });
    return map;
  }, [flattenedRows]);

  const groupSortableIds = useMemo(() => {
    const groups = new Map<string, Set<number>>();
    const parentSubtasks = new Map<number, Set<number>>();

    flattenedRows.forEach((row) => {
      if (row.type !== 'task' && row.type !== 'subtask') return;
      if ((row as any).task.isGroupContext) return; // borrowed context: not a drop target
      if (row.type === 'task') {
        const groupId = (row as any).groupId;
        if (!groups.has(groupId)) groups.set(groupId, new Set());
        groups.get(groupId)!.add((row as any).task.id);
      } else if (row.type === 'subtask') {
        const parentId = (row as any).parentId;
        if (!parentSubtasks.has(parentId)) parentSubtasks.set(parentId, new Set());
        parentSubtasks.get(parentId)!.add((row as any).task.id);
      }
    });

    return { groups, parentSubtasks };
  }, [flattenedRows]);

  // Custom collision detection - restrict to same group/parent only
  const collisionDetection = useCallback(
    (args: Parameters<typeof closestCenter>[0]) => {
      const activeId = Number(args.active.id);
      const activeInfo = taskGroupMap.get(activeId);
      if (!activeInfo) return [];

      let validIds: Set<number> | undefined;
      if (activeInfo.type === 'subtask') {
        validIds = groupSortableIds.parentSubtasks.get(activeInfo.parentId!);
      } else {
        validIds = groupSortableIds.groups.get(activeInfo.groupId!);
      }

      if (!validIds) return [];

      return closestCenter({
        ...args,
        droppableContainers: args.droppableContainers.filter((container) =>
          validIds!.has(Number(container.id))
        ),
      });
    },
    [taskGroupMap, groupSortableIds]
  );

  // Custom modifier - restrict drag to section bounds + vertical axis only
  const restrictToSection = useCallback(
    ({ transform }: any) => {
      const bounds = sectionBoundsRef.current;
      if (!bounds) {
        return { ...transform, x: 0 };
      }
      return {
        ...transform,
        x: 0,
        y: Math.min(Math.max(transform.y, bounds.minY), bounds.maxY),
      };
    },
    []
  );

  // Handle drag start - compute section bounds for the modifier
  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeId = Number(event.active.id);
      setActiveDragId(activeId);
      isDraggingRef.current = true;

      const activeInfo = taskGroupMap.get(activeId);
      if (!activeInfo) return;

      const activeIndex = flattenedRows.findIndex(
        (row) =>
          (row.type === 'task' || row.type === 'subtask') &&
          (row as any).task.id === activeId
      );
      if (activeIndex === -1) return;

      // Determine which rows belong to the same section
      const isSameSection = (row: FlattenedRow) => {
        if (activeInfo.type === 'subtask') {
          return row.type === 'subtask' && (row as any).parentId === activeInfo.parentId;
        }
        return row.type === 'task' && (row as any).groupId === activeInfo.groupId;
      };

      // Find first and last indices of same-section items
      let firstIndex = -1;
      let lastIndex = -1;
      for (let i = 0; i < flattenedRows.length; i++) {
        if (isSameSection(flattenedRows[i])) {
          if (firstIndex === -1) firstIndex = i;
          lastIndex = i;
        }
      }

      if (firstIndex === -1) return;

      // Calculate pixel offsets by summing row heights
      let offset = 0;
      let activeStart = 0;
      let firstStart = 0;
      let lastStart = 0;

      for (let i = 0; i <= lastIndex; i++) {
        if (i === firstIndex) firstStart = offset;
        if (i === activeIndex) activeStart = offset;
        if (i === lastIndex) lastStart = offset;

        const row = flattenedRows[i];
        if (row.type === 'group-header') {
          offset += i === 0 ? 50 : 70;
        } else {
          offset += 40;
        }
      }

      sectionBoundsRef.current = {
        minY: firstStart - activeStart,
        maxY: lastStart - activeStart,
      };
    },
    [flattenedRows, taskGroupMap]
  );

  // Handle drag cancel - cleanup
  const handleDragCancel = useCallback(() => {
    isDraggingRef.current = false;
    setTimeout(() => {
      setActiveDragId(null);
    }, 200);
    sectionBoundsRef.current = null;
  }, []);

  // Get task IDs for a specific group
  const getGroupTaskIds = useCallback((groupId: string) => {
    return flattenedRows
      .filter((row) => {
        // Borrowed/context rows (#2309) are read-only — never part of select-all.
        if ((row.type === 'task' || row.type === 'subtask') && (row as any).task.isGroupContext) {
          return false;
        }
        if (row.type === 'task') {
          return (row as any).groupId === groupId;
        }
        if (row.type === 'subtask') {
          const parentRow = flattenedRows.find(
            (r) => r.type === 'task' && !(r as any).task.isGroupContext && (r as any).task.id === (row as any).parentId
          );
          return parentRow && (parentRow as any).groupId === groupId;
        }
        return false;
      })
      .map((row) => (row as any).task.id);
  }, [flattenedRows]);

  const handleSelectAllInGroup = useCallback((groupId: string) => {
    const groupTaskIds = getGroupTaskIds(groupId);
    setSelectedItems([...new Set([...selectedItems, ...groupTaskIds])]);
  }, [selectedItems, getGroupTaskIds, setSelectedItems]);

  const handleDeselectAllInGroup = useCallback((groupId: string) => {
    const groupTaskIds = getGroupTaskIds(groupId);
    setSelectedItems(selectedItems.filter((itemId) => !groupTaskIds.includes(itemId)));
  }, [selectedItems, getGroupTaskIds, setSelectedItems]);

  const currentUserId = useGetUserFromAccessToken().data?.user.id;

  // Handle task update
  const handleTaskUpdate = useCallback(
    async (updatedTask: TaskTableDataType) => {
      const oldTask = flattenedRows
        .filter((row) => row.type === 'task' || row.type === 'subtask')
        .find((row) => (row as any).task.id === updatedTask.id);
      const prevStage = (oldTask as any)?.task?.stage as Task['stage'] | undefined;

      // Optimistically bump "Last edited by/at" so the task view reflects edits made
      // from the table in realtime. The cache writes below otherwise persist the stale
      // pre-edit metadata, clobbering the authoritative values the `taskUpdated` socket
      // broadcasts (the backend recomputes them and re-broadcasts). #2536
      const optimisticTask: TaskTableDataType = {
        ...updatedTask,
        lastEditedBy: currentUserId ?? updatedTask.lastEditedBy,
        updatedAt: new Date(),
      };

      try {
        // Update local dataSource immediately so row fields don't flash stale data
        setDataSource((prev) =>
          prev.map((task) => {
            if (task.id === optimisticTask.id) {
              return { ...task, ...optimisticTask };
            } else if (task.parentTaskId === optimisticTask.id) {
              return { ...task, epicId: optimisticTask.epicId };
            }
            // Update subtask within parent's subtasks array
            if (task.subtasks?.some((s) => s.id === optimisticTask.id)) {
              return {
                ...task,
                subtasks: task.subtasks.map((s) =>
                  s.id === optimisticTask.id ? { ...s, ...optimisticTask } : s
                ),
              };
            }
            return task;
          })
        );

        // Also update localFlattenedRows directly to bypass the useEffect sync delay
        setLocalFlattenedRows((prev) =>
          prev.map((row) => {
            if ((row.type === 'task' || row.type === 'subtask') && (row as any).task.id === optimisticTask.id) {
              return { ...row, task: { ...(row as any).task, ...optimisticTask } };
            }
            return row;
          })
        );

        await updateTask(Number(updatedTask.id), updatedTask);
        if (updatedTask.TaskLabels) {
          await updateTaskLabels(Number(updatedTask.id), {
            labelIds: updatedTask.TaskLabels.map((label) => label.labelId),
          });
        }

        queryClient.setQueryData(['tasks', optimisticTask.workspaceId], (oldDataSource: Task[]) => {
          return oldDataSource.map((task) => {
            if (task.id === optimisticTask.id) {
              return { ...optimisticTask, TaskLabels: optimisticTask.TaskLabels ?? task.TaskLabels };
            } else if (task.parentTaskId === optimisticTask.id) {
              return {
                ...task,
                epicId: optimisticTask.epicId,
              };
            }
            // Update subtask within parent's subtasks array
            if ((task as any).subtasks?.some((s: any) => s.id === optimisticTask.id)) {
              return {
                ...task,
                subtasks: (task as any).subtasks.map((s: any) =>
                  s.id === optimisticTask.id ? { ...s, ...optimisticTask, TaskLabels: optimisticTask.TaskLabels ?? s.TaskLabels } : s
                ),
              };
            }
            return task;
          });
        });

        if (optimisticTask.type === TaskType.epic) {
          queryClient.setQueryData(['epics', optimisticTask.workspaceId], (oldDataSource: Epic[]) => {
            return oldDataSource.map((task) => {
              return task.id === optimisticTask.id
                ? { ...optimisticTask, TaskLabels: optimisticTask.TaskLabels ?? task.TaskLabels }
                : task;
            });
          });
        }

        if (updatedTask.stage && prevStage !== updatedTask.stage) {
          reconcileStageChange(updatedTask.workspaceId, updatedTask.id, prevStage, {
            stage: updatedTask.stage,
            archivedAt: updatedTask.archivedAt,
          });
          setDataSource((prev) => {
            if (updatedTask.stage === 'archive' || (prevStage === 'triage' && updatedTask.stage !== 'triage')) {
              return prev.filter((t) => t.id !== updatedTask.id);
            }
            return prev;
          });
        }

        // Check if status is completed
        const oldTaskData = (oldTask as any)?.task;
        if (
          oldTaskData?.statusId !== updatedTask.statusId &&
          taskStatuses[updatedTask.statusId]?.group === TaskStatusGroup.completed
        ) {
          const analyticsWorkspace = resolveWorkspaceData
            ? (queryClient.getQueryData(['workspace', updatedTask.workspaceId]) as Workspace | undefined)
            : workspaceQuery.data;
          analytics.track(AnalyticsEvent.taskCompleted, taskCompletedProperties(updatedTask, analyticsWorkspace));
        }
      } catch (error) {
        console.error('Failed to update task:', error);
        void messageApi.error('Failed to update task');
      }
    },
    [flattenedRows, setDataSource, taskStatuses, workspaceQuery.data, messageApi, resolveWorkspaceData, currentUserId]
  );

  // Handle task open
  const handleTaskOpen = useCallback(
    (task: TaskTableDataType) => {
      scrollRestorationContext?.saveScrollBeforeNavigation();
      // From All Tasks, tag the navigation so the task view's Back button returns
      // to /all-tasks instead of the opened task's space default module (#2548).
      navigateToTask(task, viewId === ALL_TASKS_VIEW_ID ? { state: { from: 'all-tasks' } } : undefined);
    },
    [scrollRestorationContext, navigateToTask, viewId]
  );

  // Create row menu props function
  const getRowMenuProps = useCallback(
    (task: Task) => ({
      items: taskMenuProps.items,
      onClick: (e: any) => taskMenuProps.onClick(e, task),
    }),
    [taskMenuProps]
  );

  // Handle add task for empty groups
  const handleAddTask = useCallback(
    (group: GroupItem) => {
      let modalProps = {};
      if (groupBy === Column.epic) {
        modalProps = { epicId: group?.id };
      } else if (groupBy === Column.taskType) {
        modalProps = { taskType: group as TaskType };
      } else if (groupBy === Column.assignee) {
        modalProps = { assignee: group as User };
      } else if (groupBy === Column.status) {
        modalProps = { status: group as TaskStatus };
      } else if (groupBy === Column.estimate) {
        modalProps = { estimate: group as number };
      }
      if (viewContext?.view?.entityType === WorkspaceViewEntityType.epic) {
        modalProps = { ...modalProps, taskType: TaskType.epic };
      }
      openModal(ModalType.CreateItem, modalProps);
    },
    [groupBy, viewContext?.view?.entityType, openModal]
  );

  // Helper function to handle subtask reordering within the same parent
  const handleSubtaskReordering = useCallback(
    async (
      parentTask: TaskTableDataType,
      activeId: number,
      overId: number
    ) => {
      if (!parentTask.subtasks) return;

      const activeIndex = parentTask.subtasks.findIndex((s) => s.id === activeId);
      const overIndex = parentTask.subtasks.findIndex((s) => s.id === overId);

      if (activeIndex === -1 || overIndex === -1) {
        return;
      }

      // Prevent sync effect from overwriting during drag
      isDraggingRef.current = true;

      // Update local flattenedRows immediately for visual feedback
      const activeRowIndex = localFlattenedRows.findIndex(
        (row) => row.type === 'subtask' && (row as any).task.id === activeId
      );
      const overRowIndex = localFlattenedRows.findIndex(
        (row) => row.type === 'subtask' && (row as any).task.id === overId
      );

      if (activeRowIndex !== -1 && overRowIndex !== -1) {
        const newFlattenedRows = arrayMove(localFlattenedRows, activeRowIndex, overRowIndex);
        setLocalFlattenedRows(newFlattenedRows);
      }

      // Reorder subtasks within the parent
      const newSubtasks = arrayMove(parentTask.subtasks, activeIndex, overIndex);

      // Update dataSource
      const newDataSource = dataSource.map((task) => {
        if (task.id === parentTask.id) {
          return { ...task, subtasks: newSubtasks };
        }
        return task;
      });
      setDataSource(newDataSource);

      // Allow sync effect to run again after state settles
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 100);

      // Call backend API to update task order (includes subtasks)
      try {
        const allTaskIdsForBackend = newDataSource.flatMap((task) => [
          task.id,
          ...(task.subtasks?.map((subtask) => subtask.id) || []),
        ]);
        await updateSpaceTasksOrder(Number(workspaceQuery.data?.id), allTaskIdsForBackend, activeId, overId);
      } catch (error) {
        console.error('Failed to update subtask order:', error);
        void messageApi.error('Failed to update subtask order');
      }
    },
    [dataSource, setDataSource, localFlattenedRows, workspaceQuery.data?.id, messageApi]
  );

  // Check if two tasks share the same value for the current sort column
  const isSameSortGroup = useCallback(
    (active: TaskTableDataType, over: TaskTableDataType, sortByColumn: string | undefined): boolean => {
      return isSameSortGroupFn(active, over, sortByColumn, estimationType, activeSprintId);
    },
    [estimationType, activeSprintId]
  );

  // Handle DnD
  const handleDragEnd = useCallback(
    async ({ active, over }: DragEndEvent) => {
      // Defer z-index reset so the wrapper stays elevated during the snap-back transition
      setTimeout(() => {
        setActiveDragId(null);
      }, 200);
      sectionBoundsRef.current = null;
      isDraggingRef.current = false;

      if (!over || active.id === over.id) return;

      const activeId = Number(active.id);
      const overId = Number(over.id);

      // Find active and over tasks in flattened rows.
      // #2309: ignore borrowed/context copies so we resolve the real row, type and group.
      const activeRow = flattenedRows.find(
        (row) =>
          (row.type === 'task' || row.type === 'subtask') &&
          !(row as any).task.isGroupContext &&
          (row as any).task.id === activeId
      );
      const overRow = flattenedRows.find(
        (row) =>
          (row.type === 'task' || row.type === 'subtask') &&
          !(row as any).task.isGroupContext &&
          (row as any).task.id === overId
      );

      if (!activeRow || !overRow) return;

      // Check if active and over are subtasks
      const activeIsSubtask = activeRow.type === 'subtask';
      const overIsSubtask = overRow.type === 'subtask';

      // Case 1: Both are subtasks - check if same parent
      if (activeIsSubtask && overIsSubtask) {
        const activeParentId = (activeRow as any).parentId;
        const overParentId = (overRow as any).parentId;

        // Only allow subtask reordering within the same parent
        if (activeParentId === overParentId) {
          const sortColumn = viewContext?.view?.sortBy?.column;
          const isManualSort = !sortColumn || sortColumn === 'Manual';

          if (!isManualSort) {
            // Check all subtasks in the movement range, not just active and over
            const activeIdx = flattenedRows.findIndex(
              (row) => row.type === 'subtask' && !(row as any).task.isGroupContext && (row as any).task.id === activeId
            );
            const overIdx = flattenedRows.findIndex(
              (row) => row.type === 'subtask' && !(row as any).task.isGroupContext && (row as any).task.id === overId
            );
            if (activeIdx !== -1 && overIdx !== -1) {
              const start = Math.min(activeIdx, overIdx);
              const end = Math.max(activeIdx, overIdx);
              const wouldBreakSort = flattenedRows
                .slice(start, end + 1)
                .some(
                  (row) =>
                    row.type === 'subtask' &&
                    !(row as any).task.isGroupContext &&
                    !isSameSortGroup((activeRow as any).task, (row as any).task, sortColumn)
                );
              if (wouldBreakSort) {
                setPendingDragIds({ activeId, overId });
                setBreakSortOpen(true);
                return;
              }
            }
          }

          const parentTask = dataSource.find((t) => t.id === activeParentId);
          if (parentTask) {
            await handleSubtaskReordering(parentTask, activeId, overId);
          }
        }
        return;
      }

      // Case 2: One is subtask and other is parent - not supported
      if (activeIsSubtask || overIsSubtask) {
        return;
      }

      // Case 3: Both are parent tasks - existing logic
      const sortColumn = viewContext?.view?.sortBy?.column;
      const isManualSort = !sortColumn || sortColumn === 'Manual';

      const activeGroupId = (activeRow as any).groupId;
      const overGroupId = (overRow as any).groupId;

      if (activeGroupId !== overGroupId) {
        if (!isManualSort) {
          setPendingDragIds({ activeId, overId });
          setBreakSortOpen(true);
          return;
        }
        onSwitchToManualSort?.(activeId, overId);
        return;
      }

      if (!isManualSort) {
        // Check all tasks in the movement range, not just active and over.
        // The active and over may share the same sort value but tasks in between may not,
        // which means the reorder would break the current sort order.
        const activeIdx = flattenedRows.findIndex(
          (row) => row.type === 'task' && !(row as any).task.isGroupContext && (row as any).task.id === activeId
        );
        const overIdx = flattenedRows.findIndex(
          (row) => row.type === 'task' && !(row as any).task.isGroupContext && (row as any).task.id === overId
        );
        if (activeIdx !== -1 && overIdx !== -1) {
          const start = Math.min(activeIdx, overIdx);
          const end = Math.max(activeIdx, overIdx);
          const wouldBreakSort = flattenedRows
            .slice(start, end + 1)
            .some(
              (row) =>
                row.type === 'task' &&
                !(row as any).task.isGroupContext &&
                !isSameSortGroup((activeRow as any).task, (row as any).task, sortColumn)
            );
          if (wouldBreakSort) {
            setPendingDragIds({ activeId, overId });
            setBreakSortOpen(true);
            return;
          }
        }
      }

      // Prevent sync effect from overwriting during drag
      isDraggingRef.current = true;

      // Update local flattenedRows immediately for instant visual feedback.
      // #2309: target the real (home) row, never a borrowed/context copy in another group.
      const activeRowIndex = localFlattenedRows.findIndex(
        (row) => row.type === 'task' && !(row as any).task.isGroupContext && (row as any).task.id === activeId
      );
      const overRowIndex = localFlattenedRows.findIndex(
        (row) => row.type === 'task' && !(row as any).task.isGroupContext && (row as any).task.id === overId
      );

      if (activeRowIndex === -1 || overRowIndex === -1) {
        isDraggingRef.current = false;
        return;
      }

      // Collect the active task's block (parent + its expanded subtasks)
      let activeBlockEnd = activeRowIndex;
      for (let i = activeRowIndex + 1; i < localFlattenedRows.length; i++) {
        if (localFlattenedRows[i].type === 'subtask' && (localFlattenedRows[i] as any).parentId === activeId) {
          activeBlockEnd = i;
        } else {
          break;
        }
      }
      const activeBlock = localFlattenedRows.slice(activeRowIndex, activeBlockEnd + 1);

      // Remove the active block from the array
      const withoutActive = [
        ...localFlattenedRows.slice(0, activeRowIndex),
        ...localFlattenedRows.slice(activeBlockEnd + 1),
      ];

      // Find the over task's position in the reduced array (real row, not a context copy)
      const newOverIndex = withoutActive.findIndex(
        (row) => row.type === 'task' && !(row as any).task.isGroupContext && (row as any).task.id === overId
      );
      if (newOverIndex === -1) {
        isDraggingRef.current = false;
        return;
      }

      // Determine insert position: after the over task's block (including its subtasks)
      let insertIndex = newOverIndex;
      if (overRowIndex > activeRowIndex) {
        // Moving down: insert after the over task and its subtasks
        insertIndex = newOverIndex + 1;
        for (let i = newOverIndex + 1; i < withoutActive.length; i++) {
          if (withoutActive[i].type === 'subtask' && (withoutActive[i] as any).parentId === overId) {
            insertIndex = i + 1;
          } else {
            break;
          }
        }
      }

      // Insert the active block at the new position
      const newFlattenedRows = [
        ...withoutActive.slice(0, insertIndex),
        ...activeBlock,
        ...withoutActive.slice(insertIndex),
      ];
      setLocalFlattenedRows(newFlattenedRows);

      // Extract the new task order from the VISUAL order (localFlattenedRows), not dataSource
      // This is what the user sees and expects.
      // #2309: borrowed/context rows share their home row's task.id, so a parent can appear
      // as multiple 'task' rows across groups. Exclude context rows and dedupe — otherwise
      // the rebuilt dataSource/order below would contain the same task several times.
      const currentVisualTaskIds = Array.from(
        new Set(
          localFlattenedRows
            .filter((row) => row.type === 'task' && !(row as any).task.isGroupContext)
            .map((row) => (row as any).task.id as number)
        )
      );

      const activeVisualIndex = currentVisualTaskIds.indexOf(activeId);
      const overVisualIndex = currentVisualTaskIds.indexOf(overId);

      const newTasksOrder = arrayMove(currentVisualTaskIds, activeVisualIndex, overVisualIndex);

      // Update dataSource to match the new order
      const taskMap = new Map(dataSource.map((task) => [task.id, task]));
      const newOrderArray = newTasksOrder
        .map((id) => taskMap.get(id))
        .filter((task): task is TaskTableDataType => task !== undefined);

      // Add any tasks that weren't in the visual order (subtasks, etc.)
      dataSource.forEach((task) => {
        if (!newTasksOrder.includes(task.id)) {
          newOrderArray.push(task);
        }
      });

      // Update dataSource
      setDataSource(newOrderArray);

      // Allow sync effect to run again after state settles
      setTimeout(() => {
        isDraggingRef.current = false;
      }, 100);

      // Update order in the backend. Epics keep their own order (['epics', spaceId]),
      // so they must not be written into the space's TaskOrder. (#2544)
      // The task path does arrayMove on the cached order, preserving subtask positions.
      try {
        if (viewContext?.view?.entityType === WorkspaceViewEntityType.epic) {
          await updateSpaceEpicsOrder(Number(workspaceQuery.data?.id), newTasksOrder);
        } else {
          await updateSpaceTasksOrder(Number(workspaceQuery.data?.id), newTasksOrder, activeId, overId);
        }
      } catch (error) {
        console.error('Failed to update task order:', error);
        void messageApi.error('Failed to update task order');
      }
    },
    [localFlattenedRows, flattenedRows, onSwitchToManualSort, dataSource, setDataSource, workspaceQuery.data?.id, messageApi, handleSubtaskReordering, viewContext?.view?.sortBy?.column, viewContext?.view?.entityType, isSameSortGroup]
  );

  const onBreakSort = useCallback(() => {
    setBreakSortOpen(false);
    if (pendingDragIds) {
      onSwitchToManualSort?.(pendingDragIds.activeId, pendingDragIds.overId);
    }
    setPendingDragIds(null);
  }, [pendingDragIds, onSwitchToManualSort]);

  const onCancelBreakSort = useCallback(() => {
    setBreakSortOpen(false);
    setPendingDragIds(null);
  }, []);


  // Handle shift+click range selection
  const handleTaskSelect = useCallback(
    (taskId: number, e: React.MouseEvent) => {
      if (e.shiftKey && selectedItems.length > 0) {
        e.preventDefault();
        const currentIndex = allTaskIds.indexOf(taskId);
        const lastSelectedIndex = allTaskIds.indexOf(selectedItems[selectedItems.length - 1]);
        const start = Math.min(currentIndex, lastSelectedIndex);
        const end = Math.max(currentIndex, lastSelectedIndex);
        const rangeIds = allTaskIds.slice(start, end + 1);
        setSelectedItems([...new Set([...selectedItems, ...rangeIds])]);
      } else {
        setSelectedItems([...new Set([...selectedItems, taskId])]);
      }
    },
    [selectedItems, allTaskIds, setSelectedItems]
  );

  const handleTaskDeselect = useCallback(
    (taskId: number, e: React.MouseEvent) => {
      if (e.shiftKey && selectedItems.length > 0) {
        e.preventDefault();
        const currentIndex = allTaskIds.indexOf(taskId);
        const lastSelectedIndex = allTaskIds.indexOf(selectedItems[selectedItems.length - 1]);
        const start = Math.min(currentIndex, lastSelectedIndex);
        const end = Math.max(currentIndex, lastSelectedIndex);
        const rangeIds = allTaskIds.slice(start, end + 1);
        setSelectedItems([...new Set([...selectedItems, ...rangeIds])]);
      } else {
        setSelectedItems(selectedItems.filter((id) => id !== taskId));
      }
    },
    [selectedItems, allTaskIds, setSelectedItems]
  );

  if (!isInitialized) {
    return <></>;
  }

  // Calculate total width for fixed column layout
  const operationWidth = showMultiselect || customRowSelection ? 68 : 36;
  const hiddenColumns = viewContext?.view?.columns?.hidden || [];
  const viewEntityType = viewContext?.view?.entityType;

  const nonTitleWidth = calculateNonTitleWidth({
    operationWidth,
    hiddenColumns,
    viewEntityType,
    framework: framework ?? WorkspaceFramework.none,
    viewGroup: viewContext?.view?.group,
    showSpaceColumn,
  });
  const titleWidth = Math.max(394, window.innerWidth - sidebarWidth - nonTitleWidth);
  const totalWidth = nonTitleWidth + titleWidth;

  return (
    <div className="virtualized-task-list relative flex min-h-0 flex-1 flex-col bg-white">
      <style>{`
        .is-scrolled-horizontal .sticky-title-cell {
          box-shadow: 4px 0 8px -4px rgba(0, 0, 0, 0.1);
          clip-path: inset(0 -10px 0 0);
        }
        /* While renaming, the title cell hosts a floating overlay input (DEV-2210).
           It must escape the cell's clipping (so the input isn't cut off) while
           still clipping its own left/top edges so scrolled columns underneath
           don't bleed through the sticky cell. Allow right + downward overflow. */
        .sticky-title-cell.editing-title {
          overflow: visible;
          clip-path: inset(0px -9999px -9999px 0px) !important;
          z-index: 20;
        }
        /* Lift the whole editing row above the rows below so a multi-line overlay
           input floats over them. The left-clipping clip-path above keeps scrolled
           columns from bleeding through the sticky cells during horizontal scroll. */
        [data-index]:has(.editing-title) {
          z-index: 40 !important;
        }
        .virtualized-task-list .is-scrolled-vertical {
          box-shadow: 0 4px 8px -4px rgba(0, 0, 0, 0.1);
        }
        .virtualized-task-list .overflow-x-auto::-webkit-scrollbar {
          display: none;
        }
        /* #2674: use the browser's non-reserving overlay scrollbar so row divider
           lines reach the right edge, matching the sticky header and production.
           DEV-2538 had forced an always-visible, space-reserving ::-webkit-scrollbar
           (10px). The row list reserved that 10px while the header hides its own
           scrollbar (scrollbarWidth: none) and spans the full width, so rows ended
           10px short of the header — the reported gap. Setting the standard
           scrollbar-width property makes Chrome ignore ::-webkit-scrollbar sizing
           and use the auto-hiding overlay scrollbar (0 reserved width), so content is
           full-width. This intentionally softens DEV-2538's anti-fade behavior. */
        [data-scrollable="true"] {
          scrollbar-width: thin;
        }
      `}</style>
      {contextHolder}

      {/* Always-visible column header above scroll container */}
      <div ref={stickyHeaderWrapperRef} className="relative z-10 flex-shrink-0">
        <div
          ref={stickyHeaderRef}
          className="overflow-x-hidden overflow-y-hidden bg-neutral-200"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
        >
          <div style={{ minWidth: `${totalWidth}px` }}>
            <StickyColumnHeader
              showMultiselect={showMultiselect}
              allTasksSelected={allTaskIds.length > 0 && allTaskIds.every((id) => selectedItems.includes(id))}
              hasAnyTasks={allTaskIds.length > 0}
              hasAnySelected={selectedItems.length > 0}
              onSelectAll={() => setSelectedItems(allTaskIds)}
              onDeselectAll={() => setSelectedItems([])}
              hasRowSelection={!!customRowSelection}
              framework={framework}
              viewGroup={viewContext?.view?.group}
              viewEntityType={viewContext?.view?.entityType}
              hiddenColumns={viewContext?.view?.columns?.hidden}
              columnsOrder={viewContext?.view?.columns?.order}
              estimationType={estimationType}
              titleWidth={titleWidth}
              showSpaceColumn={showSpaceColumn}
            />
          </div>
        </div>
      </div>

      <DndContext
        sensors={readOnlyOrder ? [] : sensors}
        collisionDetection={collisionDetection}
        modifiers={[restrictToSection]}
        onDragStart={readOnlyOrder ? undefined : handleDragStart}
        onDragEnd={readOnlyOrder ? undefined : handleDragEnd}
        onDragCancel={readOnlyOrder ? undefined : handleDragCancel}
      >
        <SortableContext items={allTaskIds} strategy={verticalListSortingStrategy}>
          <div
            ref={parentRef}
            className="overflow-auto w-full" data-scrollable="true"
            style={{
              // Grow to fill the remaining space of the flex-column parent so the list
              // always reaches the viewport bottom regardless of how tall the chrome
              // above it is. This used to be `calc(100vh - 178px)` for the space
              // modules, a third viewport formula that disagreed with the two on the
              // wrappers above; `overflow-hidden` on the parent then clipped the
              // difference, which is what ate the reserved bottom space. #2673 #2357
              flex: '1 1 0%',
              minHeight: 0,
              // Disable macOS elastic bounce so the sticky column header doesn't
              // rubber-band when over-scrolling past the top edge. #2542
              overscrollBehavior: 'none',
            }}
          >
            <div
              className="relative"
              style={{
                height: `${virtualizer.getTotalSize() + FLOATING_ACTION_BAR_SPACE}px`,
                // Fill the container (background) but don't force extra scrollable
                // space — `calc(100% + Npx)` made short lists always scroll/bounce by
                // Npx with only a few tasks. The bottom ActionPanel padding already
                // lives in the explicit height above, so it only applies once the list
                // is long enough to actually scroll. #2557
                minHeight: '100%',
                minWidth: `${totalWidth}px`,
              }}
            >

              {virtualizer.getVirtualItems().map((virtualItem) => {
                const row = flattenedRows[virtualItem.index];
                const isFirstInSection = isFirstRowInSection(flattenedRows, virtualItem.index);
                const isFirstGroupHeader = row.type === 'group-header' && virtualItem.index === 0;
                const isActivelyDragging =
                  activeDragId !== null &&
                  (row.type === 'task' || row.type === 'subtask') &&
                  (row as any).task.id === activeDragId;

                return (
                  <div
                    key={row.id}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    className="absolute top-0 left-0 w-full"
                    style={{
                      transform: `translateY(${virtualItem.start}px)`,
                      zIndex: isActivelyDragging ? 9999 : 2,
                      paddingTop: row.type === 'group-header' && !isFirstGroupHeader ? GROUP_HEADER_GAP : undefined,
                    }}
                  >
                    {row.type === 'group-header' && (() => {
                      const groupId = String(row.group?.id ?? row.group ?? 'unassigned');
                      return (
                        <VirtualizedGroupHeader
                          group={row.group}
                          groupBy={groupBy}
                          stats={row.stats}
                          isExpanded={row.isExpanded}
                          onToggleExpand={() => {
                            toggleGroup(groupId);
                          }}
                          framework={framework}
                          estimationType={estimationType}
                          tasks={row.tasks}
                          groupId={groupId}
                          isHovered={hoveredGroupId === groupId}
                          onMouseEnter={() => setHoveredGroupId(groupId)}
                          onMouseLeave={() => setHoveredGroupId(null)}
                          showMultiselect={showMultiselect}
                          allTasksSelected={(() => {
                            const groupTaskIds = getGroupTaskIds(groupId);
                            return groupTaskIds.length > 0 && groupTaskIds.every((id) => selectedItems.includes(id));
                          })()}
                          hasAnySelected={selectedItems.length > 0}
                          onSelectAll={() => handleSelectAllInGroup(groupId)}
                          onDeselectAll={() => handleDeselectAllInGroup(groupId)}
                          hasRowSelection={!!customRowSelection}
                          hideGroupActions={hideGroupActions}
                        />
                      );
                    })()}

                    {(row.type === 'task' || row.type === 'subtask') && (() => {
                      const rowTask = (row as any).task as TaskTableDataType;
                      const rowWorkspaceData = resolveWorkspaceData?.(rowTask.workspaceId) ?? workspaceDataForRows;
                      const rowUsersWithAccess = resolveWorkspaceData && rowWorkspaceData.members.length > 0
                        ? rowWorkspaceData.members.map((u) => ({ userId: u.id, User: u } as WorkspaceAccessResponse))
                        : usersWithAccessToSpace;
                      return (
                      <div
                        className="flex flex-col"
                        onMouseEnter={() => {
                          const taskGroupId = (row as any).groupId;
                          if (taskGroupId) {
                            setHoveredGroupId(taskGroupId);
                          }
                        }}
                        onMouseLeave={() => setHoveredGroupId(null)}
                      >
                        {isFirstInSection && <div className="w-full h-[1px] bg-neutral-200" />}
                        <VirtualizedTaskRow
                          task={rowTask}
                          isSubtask={row.type === 'subtask'}
                          hasSubtasks={row.type === 'task' ? (row as any).hasSubtasks : false}
                          isExpanded={row.type === 'task' ? (row as any).isExpanded : false}
                          onToggleExpand={() => {
                            if (row.type === 'task') {
                              toggleSubtask((row as any).task.id);
                            }
                          }}
                          showMultiselect={showMultiselect}
                          isSelected={selectedItems.includes(rowTask.id)}
                          hasAnySelected={selectedItems.length > 0}
                          onSelect={(e) => handleTaskSelect(rowTask.id, e)}
                          onDeselect={(e) => handleTaskDeselect(rowTask.id, e)}
                          usersWithAccessToSpace={rowUsersWithAccess}
                          rowMenuProps={getRowMenuProps(rowTask)}
                          customRowSelection={customRowSelection}
                          onTaskUpdate={handleTaskUpdate}
                          onTaskOpen={handleTaskOpen}
                          onSprintToPlanningConfirm={onSprintToPlanningConfirm}
                          hiddenColumns={viewContext?.view?.columns?.hidden}
                          columnsOrder={viewContext?.view?.columns?.order}
                          showMessage={(message) => messageApi.success(message)}
                          handleEditEstimationSettings={handleEditEstimationSettings}
                          titleWidth={titleWidth}
                          workspaceData={rowWorkspaceData}
                          showSpaceColumn={showSpaceColumn}
                          onEditingTitleChange={handleEditingTitleChange}
                          resolveSpaceName={resolveSpaceName}
                        />
                        <div className="w-full h-[1px] bg-neutral-200" />
                      </div>
                      );
                    })()}

                    {row.type === 'add-task' && (
                      <div className="w-full text-neutral-900 flex items-center h-[40px] border-t border-neutral-200 bg-white">
                        {/* Full-width bar covers scrolled columns; the link is pinned
                            viewport-left via a nested sticky so it never drifts (#2561). */}
                        <div
                          className="sticky left-0 z-[1] flex items-center gap-2"
                          style={{
                            fontSize: '14px',
                            paddingLeft: showMultiselect || customRowSelection ? '100px' : '82px',
                          }}
                        >
                          <div
                            className="hover:text-[#08CF65] cursor-pointer"
                            onClick={() => handleAddTask(row.group)}
                          >
                            + Add {viewContext?.view?.entityType === WorkspaceViewEntityType.epic ? 'Epic' : 'Task'}
                          </div>
                        </div>
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          </div>
        </SortableContext>
      </DndContext>
      {!readOnlyOrder && (
        <SwitchToManualSortModal
          sortColumn={viewContext?.view?.sortBy?.column ?? ''}
          open={breakSortOpen}
          onOk={onBreakSort}
          onCancel={onCancelBreakSort}
        />
      )}
    </div>
  );
};
